import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout } from './components/Layout';
import { Auth } from './components/Auth';
import { IdeaList } from './components/IdeaList';
import { IdeaModal } from './components/IdeaModal';
import { Dashboard } from './components/Dashboard';
import { ChatInterface } from './components/ChatInterface';
import { api } from './services/api';
import { Idea, Topic, ViewMode, Priority } from './types';
import { Plus, Filter, RefreshCw, Lightbulb, Loader2, Sparkles } from 'lucide-react';

const CACHE_KEY = 'idea_notes_data';

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentView, setCurrentView] = useState<ViewMode>(ViewMode.IDEAS); // Changed default view to My Ideas per typical flow, but footer order is visual
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Data State
  const [topics, setTopics] = useState<Topic[]>([]);
  const [ideas, setIdeas] = useState<Idea[]>([]);
  
  // UI State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIdea, setEditingIdea] = useState<Idea | null>(null);

  // Filters
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterTopic, setFilterTopic] = useState<string>('all');

  // Fetch Data with Caching Strategy
  const fetchData = useCallback(async (isInitialLoad = true) => {
    if (!isAuthenticated) return;
    
    // 1. If initial load, try to load from cache first for instant UI
    if (isInitialLoad) {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const { topics: cachedTopics, ideas: cachedIdeas } = JSON.parse(cached);
          setTopics(cachedTopics);
          setIdeas(cachedIdeas);
          // If we have cache, we don't need to show full page loader, just a background refresh
          setIsLoading(false); 
          setIsRefreshing(true);
        } catch (e) {
          // If cache is corrupt, show loader
          setIsLoading(true);
        }
      } else {
        // No cache, show loader
        setIsLoading(true);
      }
    } else {
      // Manual refresh
      setIsRefreshing(true);
    }

    // 2. Network Fetch (Stale-while-revalidate)
    try {
      const data = await api.getInitialData();
      setTopics(data.topics);
      setIdeas(data.ideas);
      // Update Cache
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error("Failed to fetch data:", error);
      // Only alert if we have no data at all
      if (topics.length === 0 && ideas.length === 0) {
          alert("Failed to load data. Please check your connection.");
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [isAuthenticated, topics.length, ideas.length]);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  const handleRefresh = () => {
    fetchData(false);
  };

  // Handlers
  const handleAddTopic = async (name: string) => {
    try {
      const newTopic = await api.addTopic(name);
      setTopics(prev => {
        const updated = [...prev, newTopic];
        localStorage.setItem(CACHE_KEY, JSON.stringify({ topics: updated, ideas }));
        return updated;
      });
    } catch (error) {
      console.error(error);
      alert("Failed to add topic");
    }
  };

  const handleSaveIdea = async (ideaData: Partial<Idea>) => {
    try {
      let updatedIdeas;
      if (ideaData.id) {
        // Edit - Optimistic UI handled by re-fetch usually, but let's update local state immediately
        // Note: The API returns the saved object.
        const updated = await api.updateIdea(ideaData as Idea);
        updatedIdeas = ideas.map(i => i.id === updated.id ? { ...i, ...updated } : i);
      } else {
        // Create
        const newIdea = await api.addIdea(ideaData as Omit<Idea, 'id' | 'createdAt' | 'isDone'>);
        updatedIdeas = [...ideas, newIdea];
      }
      setIdeas(updatedIdeas);
      localStorage.setItem(CACHE_KEY, JSON.stringify({ topics, ideas: updatedIdeas }));
    } catch (error) {
      console.error(error);
      alert("Failed to save idea");
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      // Optimistic update
      const newStatus = !currentStatus;
      const optimisticIdeas = ideas.map(i => i.id === id ? { ...i, isDone: newStatus } : i);
      setIdeas(optimisticIdeas);
      localStorage.setItem(CACHE_KEY, JSON.stringify({ topics, ideas: optimisticIdeas }));
      
      const updated = await api.toggleIdeaStatus(id, newStatus);
      
      // Confirm update with server response
      setIdeas(prev => {
         const verifiedIdeas = prev.map(i => i.id === updated.id ? { ...i, ...updated } : i);
         localStorage.setItem(CACHE_KEY, JSON.stringify({ topics, ideas: verifiedIdeas }));
         return verifiedIdeas;
      });
      
    } catch (error) {
      console.error(error);
      alert("Failed to update status");
      // Revert if failed
      setIdeas(prev => {
          const reverted = prev.map(i => i.id === id ? { ...i, isDone: currentStatus } : i);
          localStorage.setItem(CACHE_KEY, JSON.stringify({ topics, ideas: reverted }));
          return reverted;
      });
    }
  };

  const activeIdeas = useMemo(() => {
     return ideas.filter(i => {
         if (i.isDone) return false;
         if (filterPriority !== 'all' && i.priority !== filterPriority) return false;
         if (filterTopic !== 'all' && i.topicId !== filterTopic) return false;
         return true;
     });
  }, [ideas, filterPriority, filterTopic]);

  const archivedIdeas = useMemo(() => ideas.filter(i => i.isDone), [ideas]);
  const pendingCount = ideas.filter(i => !i.isDone).length;

  if (!isAuthenticated) {
    return <Auth onSuccess={() => setIsAuthenticated(true)} />;
  }

  // Enhanced Loading Screen
  if (isLoading && topics.length === 0) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-secondary to-primary flex flex-col items-center justify-center p-4 z-50">
        <div className="relative mb-8">
           {/* Decorative animated rings */}
           <div className="absolute inset-0 bg-white/20 rounded-full animate-ping opacity-75"></div>
           <div className="absolute inset-0 bg-white/10 rounded-full animate-pulse delay-75 duration-1000"></div>

           {/* Main Icon Container */}
           <div className="relative bg-white p-6 rounded-full shadow-2xl z-10 flex items-center justify-center">
              <Lightbulb size={48} className="text-primary animate-[pulse_3s_ease-in-out_infinite]" />
              {/* Sparkle accent */}
              <div className="absolute -top-1 -right-1 bg-yellow-400 rounded-full p-1.5 animate-bounce shadow-sm border-2 border-primary">
                 <Sparkles size={14} className="text-white" />
              </div>
           </div>
        </div>

        <h2 className="text-3xl font-black text-white tracking-tighter mb-3 drop-shadow-md">
          Idea<span className="text-red-200">Notes</span>
        </h2>
        
        <div className="flex items-center space-x-3 text-white/90 text-sm font-medium bg-white/10 px-5 py-2.5 rounded-full backdrop-blur-md border border-white/20 shadow-lg">
           <Loader2 size={16} className="animate-spin text-white" />
           <span className="tracking-wide">Syncing your brain...</span>
        </div>
      </div>
    );
  }

  return (
    <Layout 
      currentView={currentView} 
      onChangeView={setCurrentView} 
      onLogout={() => setIsAuthenticated(false)}
      onRefresh={handleRefresh}
      isRefreshing={isRefreshing}
    >
      <>
          {currentView === ViewMode.DASHBOARD && (
            <Dashboard ideas={ideas} topics={topics} onRefresh={handleRefresh} isRefreshing={isRefreshing} />
          )}

          {currentView === ViewMode.IDEAS && (
            <div className="space-y-4 h-full flex flex-col">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex items-center space-x-3">
                    <div className="flex items-baseline space-x-2">
                        <h2 className="text-2xl font-bold text-gray-800">My Ideas</h2>
                        <span className="text-primary font-bold text-lg">({pendingCount})</span>
                    </div>
                    <button 
                        onClick={handleRefresh} 
                        disabled={isRefreshing}
                        className="p-1.5 bg-white text-gray-400 hover:text-primary rounded-full border border-gray-200 shadow-sm transition-all disabled:opacity-50"
                        title="Reload Data"
                    >
                        <RefreshCw size={16} className={`${isRefreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>
                
                <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                   <div className="flex items-center space-x-2 bg-white px-2 py-1.5 rounded-lg border border-gray-200">
                        <Filter size={16} className="text-gray-400" />
                        <select 
                            value={filterPriority} 
                            onChange={(e) => setFilterPriority(e.target.value)}
                            className="bg-transparent text-sm outline-none text-gray-600"
                        >
                            <option value="all">All Priorities</option>
                            {Object.values(Priority).map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                   </div>

                   <div className="flex items-center space-x-2 bg-white px-2 py-1.5 rounded-lg border border-gray-200">
                        <Filter size={16} className="text-gray-400" />
                        <select 
                            value={filterTopic} 
                            onChange={(e) => setFilterTopic(e.target.value)}
                            className="bg-transparent text-sm outline-none text-gray-600 max-w-[150px]"
                        >
                            <option value="all">All Topics</option>
                            {topics.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                   </div>

                    <button
                      onClick={() => {
                        setEditingIdea(null);
                        setIsModalOpen(true);
                      }}
                      className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-red-600 transition shadow-sm font-medium ml-auto md:ml-2"
                    >
                      <Plus size={18} />
                      <span>New</span>
                    </button>
                </div>
              </div>

              <div className="flex-1 overflow-auto pb-10">
                 <IdeaList 
                    ideas={activeIdeas} 
                    topics={topics} 
                    onToggleStatus={handleToggleStatus} 
                    onEdit={(idea) => {
                        setEditingIdea(idea);
                        setIsModalOpen(true);
                    }}
                 />
              </div>
            </div>
          )}

          {currentView === ViewMode.ARCHIVE && (
            <div className="space-y-6 h-full flex flex-col">
              <div className="flex items-center space-x-3">
                 <div className="flex items-center space-x-2">
                    <h2 className="text-2xl font-bold text-gray-800">Archive</h2>
                    <span className="text-gray-400 font-medium">({archivedIdeas.length})</span>
                 </div>
                 <button 
                    onClick={handleRefresh} 
                    disabled={isRefreshing}
                    className="p-1.5 bg-white text-gray-400 hover:text-primary rounded-full border border-gray-200 shadow-sm transition-all disabled:opacity-50"
                    title="Reload Data"
                >
                    <RefreshCw size={16} className={`${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <div className="flex-1 overflow-auto pb-10">
                <IdeaList 
                    ideas={archivedIdeas} 
                    topics={topics} 
                    onToggleStatus={handleToggleStatus} 
                    onEdit={() => {}} 
                    readOnly={true}
                />
              </div>
            </div>
          )}

          {currentView === ViewMode.CHAT && (
            <div className="h-full">
                <ChatInterface ideas={ideas} topics={topics} onRefresh={handleRefresh} isRefreshing={isRefreshing} />
            </div>
          )}

          <IdeaModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSave={handleSaveIdea}
            editingIdea={editingIdea}
            topics={topics}
            onAddTopic={handleAddTopic}
          />
      </>
    </Layout>
  );
};

export default App;